/**
 * HandoffContractOnly — 12-variant native picker matrix.
 *
 * Operator brief (May 22, 2026, r11):
 *   r10 clean-room test failed on real iPhone. The failure is at the
 *   native picker boundary: iPhone Photos is not delivering files
 *   into input.files on the route. Build three raw native input
 *   variants side-by-side:
 *     A. visible native input, no button, user taps the actual control
 *     B. hidden input triggered by a button
 *     C. newly-created input generated INSIDE the tap handler
 *   Each variant × 4 attribute combinations:
 *     1. accept="video/*" multiple
 *     2. accept omitted, multiple
 *     3. accept="image/*,video/*" multiple
 *     4. single-file (no multiple), accept="video/*"
 *   12 cells total. Each cell shows: clicked, change, cancel, input,
 *   files length, staged count, first filename/type/size.
 *
 * Doctrine: every cell ROUTES STAGING through `acceptPickerHandoff`
 * (operator-locked contract). What varies is HOW the input is mounted
 * and HOW the click is triggered — NOT how the resulting FileList is
 * accepted.
 *
 * Hard constraints (operator-locked):
 *   • Variant A inputs are NOT managed by React event props — handlers
 *     are attached imperatively via `addEventListener` directly on
 *     the DOM node. React only mounts the bare element.
 *   • No upload. No basket. No queue. No backend. No metadata probe.
 *   • Each variant is fully isolated — its counters and staging do
 *     not bleed into any other variant.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { acceptPickerHandoff } from "../lib/acceptPickerHandoff";

const BUILD_STAMP = "v2.may22.2026.r11-native-matrix";
const POLL_INTERVAL_MS = 200;
const POLL_DURATION_MS = 90_000;

// ── 12 variants (3 mounts × 4 attr-sets) ────────────────────────────
const MOUNTS = [
  { id: "A", label: "visible native input (no button)",  how: "user taps the actual <input> element rendered visibly. React mounts the bare element; all event listeners are attached imperatively via addEventListener." },
  { id: "B", label: "hidden input + button",             how: "hidden <input> with style absolute/1px/opacity 0; tapping the button calls inputRef.current.click()." },
  { id: "C", label: "input created INSIDE the tap handler", how: "no DOM input exists until the button is tapped. Inside the click handler we document.createElement('input'), set attrs, attach listeners, then click — all synchronously inside the user gesture." },
];

const ATTRSETS = [
  { id: "1", label: 'accept="video/*" · multiple',         attrs: { accept: "video/*",         multiple: true  } },
  { id: "2", label: "no accept · multiple",                attrs: {                            multiple: true  } },
  { id: "3", label: 'accept="image/*,video/*" · multiple', attrs: { accept: "image/*,video/*", multiple: true  } },
  { id: "4", label: 'accept="video/*" · single',           attrs: { accept: "video/*",         multiple: false } },
];

const VARIANTS = [];
for (const m of MOUNTS) {
  for (const a of ATTRSETS) {
    VARIANTS.push({
      id:      `${m.id}${a.id}`,
      mount:   m.id,
      attrs:   a.attrs,
      mountLabel: m.label,
      attrLabel:  a.label,
      how:     m.how,
    });
  }
}

function defaultCounters() {
  return {
    clicked: 0, changed: 0, canceled: 0, inputFired: 0,
    filesLen: 0, stagedCount: 0,
    firstFile: null,
    lastSignal: null,
  };
}

function applyAttrs(node, attrs) {
  node.type = "file";
  if (attrs.accept) node.setAttribute("accept", attrs.accept);
  else node.removeAttribute("accept");
  if (attrs.multiple) node.setAttribute("multiple", "");
  else node.removeAttribute("multiple");
}

export default function HandoffContractOnly() {
  // Per-variant counters. Keyed by variant id (e.g. "A1", "C4").
  const [stats, setStats] = useState(() => {
    const o = {};
    for (const v of VARIANTS) o[v.id] = defaultCounters();
    return o;
  });

  // Variant-local DOM input refs (only A & B mount a persistent input).
  const inputRefs = useRef({});         // id -> HTMLInputElement | null
  const fingerprintRefs = useRef({});   // id -> Set<string>
  const pollTimerRefs = useRef({});     // id -> intervalId
  for (const v of VARIANTS) {
    if (!fingerprintRefs.current[v.id]) fingerprintRefs.current[v.id] = new Set();
  }

  const bumpCounter = useCallback((vid, signal) => {
    setStats((s) => ({
      ...s,
      [vid]: {
        ...s[vid],
        [signal]: (s[vid][signal] || 0) + 1,
        lastSignal: signal,
      },
    }));
  }, []);

  const stageFromNode = useCallback((vid, node, source) => {
    if (!node) return;
    const fl = node.files;
    const len = fl ? fl.length : 0;
    const first = len > 0 ? fl[0] : null;
    setStats((s) => ({
      ...s,
      [vid]: {
        ...s[vid],
        filesLen: len,
        firstFile: first
          ? { name: first.name || "(unnamed)", size: first.size ?? null, type: first.type || "(no-type)" }
          : s[vid].firstFile,
        lastSignal: source,
      },
    }));
    const result = acceptPickerHandoff(node, fingerprintRefs.current[vid]);
    if (result.newCount > 0) {
      setStats((s) => ({
        ...s,
        [vid]: { ...s[vid], stagedCount: s[vid].stagedCount + result.newCount },
      }));
    }
  }, []);

  const armPolling = useCallback((vid, node) => {
    if (!node) return;
    if (pollTimerRefs.current[vid]) clearInterval(pollTimerRefs.current[vid]);
    const startedAt = Date.now();
    let lastLen = node.files ? node.files.length : 0;
    pollTimerRefs.current[vid] = setInterval(() => {
      const cur = inputRefs.current[vid] || node;
      const len = cur && cur.files ? cur.files.length : 0;
      if (len !== lastLen) {
        bumpCounter(vid, "_polledChange");
        if (len > 0) stageFromNode(vid, cur, "poll");
        lastLen = len;
      }
      if (Date.now() - startedAt > POLL_DURATION_MS) {
        clearInterval(pollTimerRefs.current[vid]);
        pollTimerRefs.current[vid] = null;
      }
    }, POLL_INTERVAL_MS);
  }, [bumpCounter, stageFromNode]);

  // Wire a DOM input node (for mount A & B). Attaches listeners
  // imperatively — React never knows about these events.
  const wireNode = useCallback((vid, node) => {
    if (!node) return;
    // De-dupe — wire once per node instance.
    if (node.dataset.handoffWired === "1") return;
    node.dataset.handoffWired = "1";
    node.addEventListener("click",  () => {
      bumpCounter(vid, "clicked");
      armPolling(vid, node);
    });
    node.addEventListener("change", () => {
      bumpCounter(vid, "changed");
      stageFromNode(vid, node, "change");
    });
    node.addEventListener("cancel", () => {
      bumpCounter(vid, "canceled");
      stageFromNode(vid, node, "cancel");
    });
    node.addEventListener("input", () => {
      bumpCounter(vid, "inputFired");
      stageFromNode(vid, node, "input");
    });
  }, [armPolling, bumpCounter, stageFromNode]);

  // For mount C — synthesise a fresh input inside the user gesture.
  const fireMountC = useCallback((variant) => {
    bumpCounter(variant.id, "clicked");
    const node = document.createElement("input");
    applyAttrs(node, variant.attrs);
    // Hidden in the DOM but actually inserted — Safari is happier
    // when the input is in the document at click time.
    Object.assign(node.style, {
      position: "absolute", width: "1px", height: "1px",
      opacity: "0", pointerEvents: "none", overflow: "hidden",
    });
    document.body.appendChild(node);
    node.addEventListener("change", () => {
      bumpCounter(variant.id, "changed");
      stageFromNode(variant.id, node, "change");
    });
    node.addEventListener("cancel", () => {
      bumpCounter(variant.id, "canceled");
      stageFromNode(variant.id, node, "cancel");
    });
    node.addEventListener("input", () => {
      bumpCounter(variant.id, "inputFired");
      stageFromNode(variant.id, node, "input");
    });
    armPolling(variant.id, node);
    // Hold onto the node for the duration of the poll so polling
    // can keep observing input.files. Also track so we can clean
    // it up later (every node added to DOM stays alive while
    // referenced).
    inputRefs.current[variant.id] = node;
    try { node.click(); } catch (_) { /* swallow */ }
  }, [armPolling, bumpCounter, stageFromNode]);

  // Cleanup on unmount.
  useEffect(() => () => {
    for (const id of Object.keys(pollTimerRefs.current)) {
      if (pollTimerRefs.current[id]) clearInterval(pollTimerRefs.current[id]);
    }
  }, []);

  // Ref callback for mounts A & B — when React mounts the DOM node,
  // we capture it and wire listeners imperatively.
  const refCallback = useCallback((vid) => (node) => {
    if (node) {
      inputRefs.current[vid] = node;
      // Re-apply attrs in case React's prop-to-attr mapping missed
      // anything (e.g. for the no-accept variant we need to ensure
      // the attribute is actually removed).
      const variant = VARIANTS.find((v) => v.id === vid);
      if (variant) applyAttrs(node, variant.attrs);
      wireNode(vid, node);
    }
  }, [wireNode]);

  return (
    <div data-testid="handoff-contract-only" className="min-h-screen bg-slate-950 text-slate-100 px-3 py-4">
      <header className="mb-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-extrabold">
          Handoff Contract Only · 12-variant native matrix
        </p>
        <h1 className="text-lg font-extrabold leading-tight">
          Which native input shape will iPhone Safari honour?
        </h1>
        <p className="text-[11px] text-slate-400 mt-1 leading-snug">
          12 variants live on this page — 3 mount kinds × 4 attribute combos. Each variant is fully isolated. Pick a video in each variant you want to test; live counters update next to each. <b>NO upload</b>. <b>NO basket</b>. <b>NO queue</b>.
        </p>
        <p data-testid="handoff-contract-only-debug-build" className="text-[10.5px] text-amber-200 font-mono mt-2">
          build {BUILD_STAMP} · route /handoff-contract-only · ua {navigator.userAgent.slice(0, 70)}…
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
        {VARIANTS.map((v) => (
          <VariantCard
            key={v.id}
            variant={v}
            stats={stats[v.id]}
            refCallback={refCallback}
            fireMountC={fireMountC}
            onTriggerB={() => inputRefs.current[v.id]?.click()}
          />
        ))}
      </div>
    </div>
  );
}

function VariantCard({ variant, stats, refCallback, fireMountC, onTriggerB }) {
  const { id, mount, attrs, mountLabel, attrLabel, how } = variant;
  const cardTone =
    stats.stagedCount > 0
      ? "border-emerald-400/50 bg-emerald-500/5"
      : stats.canceled > 0 && stats.filesLen === 0
        ? "border-amber-400/50 bg-amber-500/5"
        : stats.clicked > 0
          ? "border-slate-600/60 bg-slate-900/40"
          : "border-slate-800 bg-slate-900/30";

  return (
    <section
      data-testid={`hco-variant-${id}`}
      data-mount={mount}
      data-staged={stats.stagedCount}
      className={`rounded-xl border ${cardTone} p-2.5 text-[11px]`}
    >
      <p className="font-mono text-amber-300 text-[10px] font-extrabold tracking-wider">
        #{id} · {mountLabel}
      </p>
      <p className="text-slate-300 text-[10.5px] mt-0.5">{attrLabel}</p>
      <p className="text-slate-500 text-[9.5px] mt-0.5 leading-snug">{how}</p>

      <div className="mt-2">
        {mount === "A" && (
          // Variant A — visible native input, NO React event props.
          // React mounts the bare <input>; we attach listeners on the
          // DOM node via refCallback → wireNode → addEventListener.
          <input
            ref={refCallback(id)}
            type="file"
            data-testid={`hco-variant-${id}-input`}
            className="w-full text-[11px] text-slate-200 bg-slate-800 border border-slate-700 rounded px-2 py-1.5"
          />
        )}
        {mount === "B" && (
          <>
            <input
              ref={refCallback(id)}
              type="file"
              data-testid={`hco-variant-${id}-input`}
              style={{
                position: "absolute", width: 1, height: 1,
                opacity: 0, pointerEvents: "none", overflow: "hidden",
              }}
            />
            <button
              type="button"
              data-testid={`hco-variant-${id}-trigger`}
              onClick={onTriggerB}
              className="w-full rounded bg-amber-400 hover:bg-amber-300 text-slate-950 text-[11px] font-extrabold px-2 py-1.5"
            >
              Pick (B)
            </button>
          </>
        )}
        {mount === "C" && (
          // No persistent input — the button creates one INSIDE the
          // tap handler. No `<input>` element exists in this card's
          // DOM until the user taps.
          <button
            type="button"
            data-testid={`hco-variant-${id}-trigger`}
            onClick={() => fireMountC(variant)}
            className="w-full rounded bg-indigo-500 hover:bg-indigo-400 text-white text-[11px] font-extrabold px-2 py-1.5"
          >
            Pick (C — fresh input)
          </button>
        )}
      </div>

      {/* Counters — per-variant, never bleed across cells. */}
      <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-0.5 font-mono text-[10px]">
        <Counter k="clicked"    v={stats.clicked}    tid={`hco-${id}-clicked`} />
        <Counter k="change"     v={stats.changed}    tid={`hco-${id}-changed`} />
        <Counter k="cancel"     v={stats.canceled}   tid={`hco-${id}-canceled`} />
        <Counter k="input"      v={stats.inputFired} tid={`hco-${id}-input`} />
        <Counter k="files_len"  v={stats.filesLen}   tid={`hco-${id}-fileslen`} tone={stats.filesLen > 0 ? "green" : "muted"} />
        <Counter k="staged"     v={stats.stagedCount} tid={`hco-${id}-staged`}  tone={stats.stagedCount > 0 ? "green" : "muted"} />
      </div>

      {stats.firstFile ? (
        <p
          data-testid={`hco-${id}-firstfile`}
          className="mt-1.5 font-mono text-[10px] text-emerald-200 break-all leading-snug"
        >
          <span className="text-emerald-400">first:</span> {stats.firstFile.name} · {stats.firstFile.type} · {stats.firstFile.size} B
        </p>
      ) : (
        <p
          data-testid={`hco-${id}-firstfile`}
          data-empty="1"
          className="mt-1.5 font-mono text-[10px] text-slate-600 italic"
        >
          no file detected yet
        </p>
      )}
      {stats.lastSignal && (
        <p className="mt-0.5 font-mono text-[9.5px] text-slate-500">
          last signal: <span className="text-slate-300">{stats.lastSignal}</span>
        </p>
      )}
    </section>
  );
}

function Counter({ k, v, tid, tone = "default" }) {
  const tones = {
    default: "text-slate-300",
    muted:   "text-slate-500",
    green:   "text-emerald-300 font-bold",
  };
  return (
    <span data-testid={tid} className="flex justify-between">
      <span className="text-slate-500">{k}</span>
      <span className={tones[tone] || tones.default}>{v}</span>
    </span>
  );
}
